import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { ReceptieParseResponse } from '@forest/contracts';
import type {
  ImportCommitInput,
  ImportCommitResponse,
  ImportParseResponse,
} from '@forest/contracts';

/** One row (or archive) an import could not take; see ImportProblems. */
export type ImportProblem = {
  rawIndex: number;
  code: string;
  reason: string;
  fields?: string[];
  context?: Record<string, string>;
};

export type ImportColumnReport = {
  found: string[];
  rows: number;
  fields: Array<{
    field: string;
    requirement: 'required' | 'fallback' | 'optional';
    expected: string[];
    column: string | null;
    filled: number;
  }>;
};

/** A row in the shapefile import's editing step. `missing` lists the
 *  fields the parser could not fill (required ones, and `ucf`). */
export type EditableShpRow = {
  rawIndex: number;
  raion: string | null;
  primarie: string | null;
  codPrimarie: string | null;
  anul: number | null;
  ucf: string | null;
  suprafata: number;
  entitateSilvica: string | null;
  ocolSilvic: string | null;
  missing: string[];
};

export type ImportTarget = 'parcel' | 'photo' | 'parcel-shp' | 'work' | 'receptie' | 'subparcel';

/** Shape returned by the works branch of /parcels/import/parse. */
export type WorkParseResponse = {
  batchId: string;
  sheet: string;
  importTarget: 'work';
  entitateSilvica: string | null;
  validCount: number;
  invalidCount: number;
  stats: { baza: number; arhiv: number; valid: number; invalid: number; totalMismatches: number };
  preview: Array<{
    row: number;
    source: 'baza' | 'arhiv';
    isArchived: boolean;
    year: number | null;
    planFapt: 'PLAN' | 'FAPT' | null;
    entitateSilvica: string | null;
    ocolSilvic: string | null;
    primarieComposite: string | null;
    ucfPlus: string | null;
    workType: string | null;
    totalFinal: number | null;
    valid: boolean;
  }>;
  errors: Array<{ row: number; source: string; path: string; code: string; message: string }>;
};

export function useWorkImportParse() {
  return useMutation({
    mutationFn: async ({ file, entitateSilvica }: { file: File; entitateSilvica?: string }) => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('target', 'work');
      if (entitateSilvica) fd.append('entitateSilvica', entitateSilvica);
      return api.post('parcels/import/parse', { body: fd }).json<WorkParseResponse>();
    },
  });
}

export function useImportParse() {
  return useMutation({
    mutationFn: async ({ file, target }: { file: File; target?: ImportTarget }) => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      if (target) {
        fd.append('target', target);
      }
      return api.post('parcels/import/parse', { body: fd }).json<ImportParseResponse>();
    },
  });
}

/**
 * Shape returned by /parcels/import/shapefile/parse. The preview rows
 * differ from the xlsx parser because shapefile data carries the
 * MOLDREF99-reprojected geometry + the extra plot-level identifiers
 * (parcelaNumber, ua, codPrimarie) and the COD_UNIC source key.
 */
export type ShapefileParseResponse = {
  batchId: string;
  sourceType: 'shapefile';
  reprojected: boolean;
  /** 'ucf' = the subdivision layer; 'teren' = the parent land plots. */
  layerKind: 'ucf' | 'teren';
  /** Rows the source listed twice with an identical outline. */
  duplicatesDropped: number;
  /** Rows given a key suffix because they collided with a different polygon. */
  keysDisambiguated: number;
  /** Teren rows skipped because UCF polygons already cover that ground. */
  coveredByUcf: number;
  /** True when a teren upload matched no UCF polygons at all. */
  terenCoverageMissing: boolean;
  /** Where each row's year came from: its column, the year typed at
   *  import, or the year of its survey date (`Data`). */
  yearSources?: { file: number; default: number; data: number };
  /** Rows whose missing `Cod_prima` was found in the register by name. */
  codesFromName?: number;
  normalized: {
    entitateMatched: number;
    entitateUnmatched: number;
    primarieMatched: number;
    primarieUnmatched: number;
    primarieRenamed: number;
    raionRenamed: number;
  };
  preview: Array<{
    rawIndex: number;
    projectUcfKey: string;
    codUnic: string | null;
    sequenceId: number | null;
    raion: string;
    primarie: string;
    entitateSilvica: string | null;
    codProiect: string | null;
    suprafata: number;
    ucf: string;
    anul: number;
    parcelaNumber: string | null;
    ua: string | null;
    codPrimarie: string | null;
  }>;
  validCount: number;
  invalidCount: number;
  errors: ImportProblem[];
  /** Problems grouped by code (all of them, not just the listed ones). */
  problemSummary?: Array<{ code: string; count: number; sample: string; fields: string[] }>;
  /** Which file column feeds each field the import reads. */
  columns?: ImportColumnReport;
  importTarget: 'parcel-shp';
};

/**
 * Shapefile parse takes the entire .shp/.dbf/.prj/.shx sidecar set in a
 * single multipart upload. Backend infers projection from .prj and
 * reprojects to WGS84 in one pass.
 */
export function useShapefileImportParse() {
  return useMutation({
    mutationFn: async ({ files, defaultYear }: { files: File[]; defaultYear?: number }) => {
      const fd = new FormData();
      // Fields before files: the server reads the multipart stream in order.
      if (defaultYear) fd.append('defaultYear', String(defaultYear));
      for (const f of files) fd.append('file', f, f.name);
      return api
        .post('parcels/import/shapefile/parse', { body: fd })
        .json<ShapefileParseResponse>();
    },
  });
}

/** Shape returned by /parcels/import/subparcels/parse. */
export type SubParcelParseResponse = {
  batchId: string;
  sourceType: 'shapefile';
  reprojected: boolean;
  toCreate: number;
  toUpdate: number;
  unchanged: number;
  preview: Array<{
    rawIndex: number;
    ucf: string;
    number: string;
    action: 'create' | 'update' | 'unchanged';
    parentKey: string;
    parentUcf: string;
    raion: string;
    primarie: string;
    anul: number;
    suprafata: number;
    suprafataCalculata: number;
  }>;
  validCount: number;
  invalidCount: number;
  errors: ImportProblem[];
  /** Problems grouped by code (all of them, not just the listed ones). */
  problemSummary?: Array<{ code: string; count: number; sample: string; fields: string[] }>;
  /** Which file column feeds each field the import reads. */
  columns?: ImportColumnReport;
  importTarget: 'subparcel';
};

export function useSubParcelImportParse() {
  return useMutation({
    mutationFn: async ({ files }: { files: File[] }) => {
      const fd = new FormData();
      for (const f of files) fd.append('file', f, f.name);
      return api
        .post('parcels/import/subparcels/parse', { body: fd, timeout: 180_000 })
        .json<SubParcelParseResponse>();
    },
  });
}

/**
 * Recepție-tehnică upload. Each archive travels with its path RELATIVE
 * to the picked folder, because the campaign year and season live only
 * in that folder's name.
 *
 * The path is percent-encoded into the filename slot: multipart parsers
 * keep only the BASENAME of a filename (busboy strips everything up to
 * the last `/` or `\`, which is what stops an upload escaping its
 * directory), so a raw path arrives as just `a0.zip` and the campaign is
 * lost. `encodeURIComponent` has no separator to collide with and
 * round-trips exactly, including a literal `%`.
 */
export function useReceptieImportParse() {
  return useMutation({
    mutationFn: async ({
      files,
      fallback,
    }: {
      files: Array<{ file: File; relativePath: string }>;
      fallback?: { anul: number; sezon: 'PRIMAVARA' | 'TOAMNA' };
    }) => {
      const fd = new FormData();
      for (const f of files) fd.append('files', f.file, encodeURIComponent(f.relativePath));
      if (fallback) {
        fd.append('anul', String(fallback.anul));
        fd.append('sezon', fallback.sezon);
      }
      return api
        .post('parcels/import/receptii/parse', { body: fd, timeout: 180_000 })
        .json<ReceptieParseResponse>();
    },
  });
}

export function useImportCommit() {
  return useMutation({
    mutationFn: (data: ImportCommitInput) =>
      api.post('parcels/import/commit', { json: data }).json<ImportCommitResponse>(),
  });
}
